export type ProductIdentitySource = {
  id?: number | null;
  product_id?: number | null;
  productId?: number | null;
  public_id?: string | null;
  publicId?: string | null;
  sku?: string | null;
  barcode?: string | null;
  clientId?: string | null;
  brand?: string | null;
  name?: string | null;
  image_url?: string | null;
  imageUrl?: string | null;
};

export type ProductReference = {
  productId?: number;
  publicId?: string;
  sku?: string;
  barcode?: string;
  clientId?: string;
  brand?: string;
  name?: string;
  imageUrl?: string | null;
};

export type ProductPairTransfer = {
  products: [ProductReference?, ProductReference?];
};

export type ComparisonDraftV1 = {
  version: 1;
  products: ProductPairTransfer['products'];
};

export type CompatibilityUsage = 'auto' | 'leave_on' | 'leave_off';

export type CompatibilityDraftV1 = {
  version: 1;
  products: ProductPairTransfer['products'];
  usage: { a: CompatibilityUsage; b: CompatibilityUsage };
};

export const COMPARISON_DRAFT_STORAGE_KEY = 'kivo:comparison-draft:v1';
export const COMPATIBILITY_DRAFT_STORAGE_KEY = 'kivo:compatibility-draft:v1';

const optionalText = (value?: string | null): string | undefined => {
  const normalized = value?.trim();
  return normalized === '' ? undefined : normalized;
};

const positiveId = (value?: number | null): number | undefined =>
  typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : undefined;

const createClientId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `product-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const createProductReference = (product: ProductIdentitySource): ProductReference => {
  const productId = positiveId(product.productId ?? product.product_id ?? product.id);
  const publicId = optionalText(product.publicId ?? product.public_id);
  const sku = optionalText(product.sku);
  const barcode = optionalText(product.barcode);
  const existingClientId = optionalText(product.clientId);
  const hasPersistentIdentity = [productId, publicId, sku, barcode].some(Boolean);

  return {
    ...(productId ? { productId } : {}),
    ...(publicId ? { publicId } : {}),
    ...(sku ? { sku } : {}),
    ...(barcode ? { barcode } : {}),
    ...(existingClientId || !hasPersistentIdentity
      ? { clientId: existingClientId ?? createClientId() }
      : {}),
    ...(optionalText(product.brand) ? { brand: optionalText(product.brand) } : {}),
    ...(optionalText(product.name) ? { name: optionalText(product.name) } : {}),
    ...('imageUrl' in product || 'image_url' in product
      ? { imageUrl: product.imageUrl ?? product.image_url ?? null }
      : {}),
  };
};

export const getProductKey = (product: ProductReference): string => {
  if (product.productId) return `id:${product.productId}`;
  if (product.publicId) return `public:${product.publicId}`;
  if (product.sku) return `sku:${product.sku}`;
  if (product.barcode) return `barcode:${product.barcode}`;
  if (product.clientId) return `client:${product.clientId}`;
  throw new Error('Product has no usable identifier');
};

const normalizedIdentityText = (value?: string): string | undefined =>
  optionalText(value)?.toLowerCase();

export const areSameProductReferences = (
  first?: ProductReference,
  second?: ProductReference,
): boolean => {
  if (!first || !second) return false;
  if (first.productId && second.productId && first.productId === second.productId) return true;

  const textKeys: (keyof Pick<ProductReference, 'publicId' | 'sku' | 'barcode' | 'clientId'>)[] = [
    'publicId', 'sku', 'barcode', 'clientId',
  ];
  return textKeys.some((key) => {
    const firstValue = normalizedIdentityText(first[key]);
    const secondValue = normalizedIdentityText(second[key]);
    return Boolean(firstValue && secondValue && firstValue === secondValue);
  });
};

const productReferenceFromUnknown = (value: unknown): ProductReference | undefined => {
  if (typeof value !== 'object' || value === null) return undefined;
  const candidate = value as ProductIdentitySource;
  const hasIdentity = [
    positiveId(candidate.productId ?? candidate.product_id ?? candidate.id),
    optionalText(candidate.publicId ?? candidate.public_id),
    optionalText(candidate.sku),
    optionalText(candidate.barcode),
    optionalText(candidate.clientId),
  ].some(Boolean);
  return hasIdentity ? createProductReference(candidate) : undefined;
};

export const readComparisonDraft = (storage: Storage = window.sessionStorage): ComparisonDraftV1 | null => {
  try {
    const raw = storage.getItem(COMPARISON_DRAFT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { version?: unknown; products?: unknown };
    if (parsed.version !== 1 || !Array.isArray(parsed.products)) {
      storage.removeItem(COMPARISON_DRAFT_STORAGE_KEY);
      return null;
    }
    const products: ProductPairTransfer['products'] = [
      productReferenceFromUnknown(parsed.products[0]),
      productReferenceFromUnknown(parsed.products[1]),
    ];
    if (!products[0] && !products[1]) {
      storage.removeItem(COMPARISON_DRAFT_STORAGE_KEY);
      return null;
    }
    return { version: 1, products };
  } catch {
    storage.removeItem(COMPARISON_DRAFT_STORAGE_KEY);
    return null;
  }
};

export const writeComparisonDraft = (
  products: ProductPairTransfer['products'],
  storage: Storage = window.sessionStorage,
): void => {
  try {
    if (!products[0] && !products[1]) {
      storage.removeItem(COMPARISON_DRAFT_STORAGE_KEY);
      return;
    }
    const draft: ComparisonDraftV1 = { version: 1, products };
    storage.setItem(COMPARISON_DRAFT_STORAGE_KEY, JSON.stringify(draft));
  } catch {
    /* sessionStorage can be unavailable in restricted browser contexts */
  }
};

const isCompatibilityUsage = (value: unknown): value is CompatibilityUsage =>
  value === 'auto' || value === 'leave_on' || value === 'leave_off';

export const readCompatibilityDraft = (storage: Storage = window.sessionStorage): CompatibilityDraftV1 | null => {
  try {
    const raw = storage.getItem(COMPATIBILITY_DRAFT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { version?: unknown; products?: unknown; usage?: { a?: unknown; b?: unknown } };
    const usageA = parsed.usage?.a;
    const usageB = parsed.usage?.b;
    if (
      parsed.version !== 1 ||
      !Array.isArray(parsed.products) ||
      !isCompatibilityUsage(usageA) ||
      !isCompatibilityUsage(usageB)
    ) {
      storage.removeItem(COMPATIBILITY_DRAFT_STORAGE_KEY);
      return null;
    }
    const products: ProductPairTransfer['products'] = [
      productReferenceFromUnknown(parsed.products[0]),
      productReferenceFromUnknown(parsed.products[1]),
    ];
    if (!products[0] && !products[1]) {
      storage.removeItem(COMPATIBILITY_DRAFT_STORAGE_KEY);
      return null;
    }
    return { version: 1, products, usage: { a: usageA, b: usageB } };
  } catch {
    storage.removeItem(COMPATIBILITY_DRAFT_STORAGE_KEY);
    return null;
  }
};

export const writeCompatibilityDraft = (
  products: ProductPairTransfer['products'],
  usage: CompatibilityDraftV1['usage'],
  storage: Storage = window.sessionStorage,
): void => {
  try {
    if (!products[0] && !products[1]) {
      storage.removeItem(COMPATIBILITY_DRAFT_STORAGE_KEY);
      return;
    }
    const draft: CompatibilityDraftV1 = { version: 1, products, usage };
    storage.setItem(COMPATIBILITY_DRAFT_STORAGE_KEY, JSON.stringify(draft));
  } catch {
    /* sessionStorage can be unavailable in restricted browser contexts */
  }
};

export const getProductDisplayValue = (product?: ProductReference): string => {
  if (!product) return '';
  if (product.barcode) return product.barcode;
  const label = [product.brand, product.name].filter(Boolean).join(' ').trim();
  if (label) return label;
  if (product.sku) return `SKU ${product.sku}`;
  if (product.productId) return `Product #${product.productId}`;
  return '';
};

export const getProductRoute = (
  pathname: '/comparison' | '/compatibility',
  products: ProductPairTransfer['products'],
): string => {
  const params = new URLSearchParams();
  for (const [index, product] of products.entries()) {
    if (!product) continue;
    const side = index === 0 ? 'a' : 'b';
    if (product.productId) params.set(`${side}_id`, String(product.productId));
    if (product.barcode) params.set(`${side}_barcode`, product.barcode);
  }
  const query = params.toString();
  return query ? `${pathname}?${query}` : pathname;
};

export const readProductPairFromSearch = (search: string): ProductPairTransfer['products'] => {
  const params = new URLSearchParams(search);
  const readSide = (side: 'a' | 'b'): ProductReference | undefined => {
    const rawId = params.get(`${side}_id`);
    const parsedId = rawId ? Number(rawId) : undefined;
    const productId = positiveId(parsedId);
    const barcode = optionalText(params.get(`${side}_barcode`));
    return productId || barcode ? createProductReference({ productId, barcode }) : undefined;
  };
  return [readSide('a'), readSide('b')];
};

export const mergeProductPairs = (
  preferred?: ProductPairTransfer['products'],
  fallback?: ProductPairTransfer['products'],
): ProductPairTransfer['products'] => [preferred?.[0] ?? fallback?.[0], preferred?.[1] ?? fallback?.[1]];
